import {
  createContext,
  useContext,
  createSignal,
  createEffect,
  on,
} from 'solid-js';
import { unwrap } from 'solid-js/store';
import { spec, specTrack } from 'framework';
import { schemaValidator } from 'schemas';

const LayerContext = createContext({});
const useLayers = () => useContext(LayerContext);

const LayerProvider = (props) => {
  const [layerInstances, setLayerInstances] = createSignal({});
  const [legendLayers, setLegendLayers] = createSignal([]);
  const [activeLayer, setActiveLayer] = createSignal(null);
  const [visibleLayers, _setVisibleLayers] = createSignal([]);

  createEffect(
    on(
      () => specTrack(props.layers),
      () => {
        if (!props.layers) return;
        let isFetching = true;
        const layerLoader = async (lyrspec) => {
          const layerType = lyrspec.layerSpec.layerType;
          const layermod = await import(
            `../../components/worldlayers/${layerType}/src/index.ts`
          );
          const schema = await import(
            `../../components/worldlayers/${layerType}/schemas/layerspec.json`
          );
          const validate = schemaValidator.compile(schema);
          if (validate(lyrspec.layerSpec)) {
            console.log('Loaded layer ' + lyrspec.id);
            return new layermod.default(lyrspec);
          } else {
            validate.errors.map((error) =>
              console.log(
                `Spec for layer ${lyrspec.id} does not validate: ${error.instancePath} -> ${error.message} [${JSON.stringify(error.params)}]`,
              ),
            );
            return null;
          }
        };
        const layerloads = props.layers.map((spec) =>
          layerLoader(unwrap(spec)),
        );
        const newLayerInstances = {};
        const newVisibleLayers = [];
        const newLegendLayers = [];
        Promise.all(layerloads).then((loaded) => {
          loaded.map((lyrinstance) => {
            if (lyrinstance) {
              newLayerInstances[lyrinstance.id] = lyrinstance;
              if (lyrinstance.visible) newVisibleLayers.push(lyrinstance.id);
              if (lyrinstance.layerSpec.legend)
                newLegendLayers.push(lyrinstance);
            }
          });
          setLayerInstances(newLayerInstances);
          setLegendLayers(newLegendLayers);
          setVisibleLayers(newVisibleLayers);
        });
        return () => {
          isFetching = false;
        };
      },
    ),
  );

  const setVisibleLayers = (requestedVisibleIdsArg) => {
    const requestedIds = [...unwrap(requestedVisibleIdsArg)];
    const instances = layerInstances();

    // Helper: get the root layer id (following linked chain up)
    const getRootLayerId = (id: string): string => {
      const layer = instances[id];
      if (layer?.linked && instances[layer.linked]) {
        return getRootLayerId(layer.linked);
      }
      return id;
    };

    // Helper: get all layers in a linked group (layer + all its followers)
    const getLinkedGroup = (rootId: string): Set<string> => {
      const group = new Set<string>([rootId]);
      for (const [id, layer] of Object.entries(instances)) {
        if (layer.linked && getRootLayerId(id) === rootId) {
          group.add(id);
        }
      }
      return group;
    };

    // Helper: check if layerA allows layerB (considering linked groups)
    const allowsLayer = (layerAId: string, layerBId: string): boolean => {
      const layerA = instances[layerAId];
      if (!layerA) return false;

      // null/undefined showWith means no restrictions
      if (layerA.showWith === null || layerA.showWith === undefined) {
        return true;
      }

      // Empty array means exclusive (no other layers allowed)
      if (Array.isArray(layerA.showWith) && layerA.showWith.length === 0) {
        return false;
      }

      // Check if B (or B's root) is in A's showWith list
      const rootB = getRootLayerId(layerBId);
      return layerA.showWith.includes(layerBId) || layerA.showWith.includes(rootB);
    };

    // Helper: check if two layers are compatible
    const areCompatible = (idA: string, idB: string): boolean => {
      if (idA === idB) return true;

      // Layers in the same linked group are always compatible
      const rootA = getRootLayerId(idA);
      const rootB = getRootLayerId(idB);
      if (rootA === rootB) return true;

      // Both must allow each other
      return allowsLayer(idA, idB) && allowsLayer(idB, idA);
    };

    // Filter to only valid requested layers (excluding linked followers, they're auto-added)
    const requestedRoots = requestedIds
      .filter((id) => instances[id] && !instances[id].linked)
      .map((id) => id);

    // Build compatible set: process in order, later layers override conflicts
    const visibleRoots = new Set<string>();

    for (const id of requestedRoots) {
      // Check compatibility with all currently visible roots
      const incompatible = Array.from(visibleRoots).filter(
        (existingId) => !areCompatible(id, existingId),
      );

      if (incompatible.length > 0) {
        // New layer conflicts with existing - remove incompatible ones
        incompatible.forEach((incompatibleId) => visibleRoots.delete(incompatibleId));
      }

      visibleRoots.add(id);
    }

    // Expand to include all linked followers
    const finalVisibleSet = new Set<string>();
    for (const rootId of visibleRoots) {
      getLinkedGroup(rootId).forEach((id) => finalVisibleSet.add(id));
    }

    _setVisibleLayers(Array.from(finalVisibleSet));
  };

  const zoomVisibility = (id, zoom) => {
    const layer = layerInstances()[id];
    const hidden = zoom < layer.minZoom || zoom > layer.maxZoom;
    return !hidden;
  };

  return (
    <LayerContext.Provider
      value={{
        layerInstances,
        legendLayers,
        activeLayer,
        setActiveLayer,
        visibleLayers,
        setVisibleLayers,
        zoomVisibility,
      }}
    >
      {props.children}
    </LayerContext.Provider>
  );
};

export { useLayers, LayerProvider };
